import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  Banknote,
  Building2,
  Globe,
  LayoutDashboard,
  MapPinned,
  MessageSquareWarning,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  isAdminNavChildActive,
  isAdminNavSectionActive,
  visibleAdminNavItems,
} from '../../../constants/adminNav';

const ICONS = {
  LayoutDashboard,
  MessageSquareWarning,
  ShieldCheck,
  Settings,
  Users,
  Building2,
  Activity,
  Banknote,
  Globe,
  MapPinned,
  ScrollText,
};

export default function AdminSidebar({ role, onNavigate }) {
  const { t } = useLanguage();
  const location = useLocation();
  const pathname = location.pathname;
  const items = visibleAdminNavItems(role);

  return (
    <nav className="flex flex-col gap-1 p-3">
      <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {t('admin.shell.brand')}
      </p>
      {items.map((item) => {
        const Icon = ICONS[item.icon] || LayoutDashboard;
        const children = Array.isArray(item.children) ? item.children : [];
        const hasChildren = children.length > 0;
        const sectionActive = isAdminNavSectionActive(item, pathname);

        return (
          <div key={item.id} className="space-y-0.5">
            <NavLink
              to={item.path}
              end={!hasChildren && item.path === '/admin'}
              onClick={onNavigate}
              aria-current={hasChildren ? (sectionActive ? 'true' : undefined) : undefined}
              className={({ isActive }) => {
                if (hasChildren) {
                  return `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                    sectionActive
                      ? 'bg-indigo-50 text-indigo-800'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`;
                }
                return `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                  isActive ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                }`;
              }}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{t(item.labelKey)}</span>
            </NavLink>

            {hasChildren ? (
              <div
                className="ml-3 space-y-0.5 border-l border-slate-200 pl-2"
                role="group"
                aria-label={t(item.labelKey)}
              >
                {children.map((child) => {
                  const childActive = isAdminNavChildActive(child, pathname);
                  return (
                    <NavLink
                      key={child.id}
                      to={child.path}
                      end={child.end === true || child.path === '/admin/prospection'}
                      onClick={onNavigate}
                      aria-current={childActive ? 'page' : undefined}
                      className={() =>
                        `block rounded-md px-2.5 py-1.5 text-[13px] font-medium ${
                          childActive
                            ? 'border-l-2 border-indigo-600 bg-indigo-600 pl-2 text-white'
                            : 'border-l-2 border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`
                      }
                    >
                      {t(child.labelKey)}
                    </NavLink>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
